import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import type { User } from '../types/users.types';
import type { BajaRespuesta, TiendaRef } from '../../negocio/negocio.types';
import { SesionesService } from '../services/sesiones.service';
import {
  AlcanceMetasMin,
  armarMotivo,
  esCuentaCompartida,
  etiquetaAlcanceNegocio,
  MOTIVOS,
  textoAlcances,
} from '../utils/sesiones';
import { mensajeDeError } from '../../negocio/errores';
import { MotivoCampos } from './MotivoCampos';

interface Props {
  usuario: User | null;
  /** Sus alcances de Metas (de `GET /metas-colaborador/accesos`), para decir qué se retira. */
  alcancesMetas: AlcanceMetasMin[];
  esPropia: boolean;
  onHide: () => void;
  onHecho: (r: BajaRespuesta) => void;
  onAsignarResponsables: (tiendas: TiendaRef[]) => void;
}

const nombreTienda = (t: TiendaRef) => `${t.cadena} ${t.num} · ${t.nombre}`;

/**
 * «Dar de baja (rotación)» en un solo paso (Consulta CM, fase 0): cierra sus
 * sesiones y conexiones, desactiva la cuenta, retira sus alcances (Metas y
 * Consulta CM) y su perfil, y deja registro. Si era supervisor o regional, la
 * respuesta dice qué tiendas quedaron sin responsable y lleva a asignarlas.
 *
 * Las cuentas de tienda y bodega no se dan de baja por rotación (la maqueta y
 * la API lo impiden): su gente rota, la cuenta se queda.
 */
export const BajaDialog: React.FC<Props> = ({ usuario, alcancesMetas, esPropia, onHide, onHecho, onAsignarResponsables }) => {
  const [motivo, setMotivo] = useState<string>(MOTIVOS[0]);
  const [nota, setNota] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [resultado, setResultado] = useState<BajaRespuesta | null>(null);

  useEffect(() => {
    setMotivo(MOTIVOS[0]);
    setNota('');
    setError(null);
    setResultado(null);
  }, [usuario?.id]);

  if (!usuario) return null;
  const compartida = esCuentaCompartida(usuario.role);
  const bloqueo = esPropia
    ? 'No puede dar de baja su propia cuenta.'
    : compartida
      ? 'Las cuentas de tienda no se dan de baja por rotación: cierre sus sesiones o cambie la contraseña.'
      : null;
  const alcances = textoAlcances(alcancesMetas, usuario.alcancesNegocio ?? []);
  const quien = usuario.name || usuario.email;

  const enviar = async () => {
    setOcupado(true);
    setError(null);
    try {
      const r = await SesionesService.baja(usuario.id, { motivo: armarMotivo(motivo, nota) });
      setResultado(r);
      onHecho(r);
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  };

  if (resultado) {
    const huerfanas = resultado.tiendasSinResponsable;
    return (
      <Dialog
        header="Cuenta dada de baja"
        visible
        onHide={onHide}
        style={{ width: '34rem' }}
        breakpoints={{ '640px': '95vw' }}
        footer={
          <div className="flex justify-end gap-2">
            {huerfanas.length > 0 && (
              <Button
                label="Asignar responsable"
                icon="pi pi-sitemap"
                onClick={() => onAsignarResponsables(huerfanas)}
              />
            )}
            <Button label="Listo" className="p-button-text" onClick={onHide} />
          </div>
        }
      >
        <div className="space-y-3 text-sm text-gray-700">
          <p>
            <b>{quien}</b> quedó desactivada.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Conexiones en vivo cerradas: {resultado.socketsCerrados}.</li>
            <li>Alcances de Metas retirados: {resultado.alcancesMetasRevocados}.</li>
            <li>Alcances de Consulta CM retirados: {resultado.alcancesNegocioRevocados}.</li>
            <li>{resultado.perfilRetirado ? 'Perfil retirado.' : 'No tenía perfil.'}</li>
          </ul>
          {huerfanas.length > 0 ? (
            <div className="rounded-md border border-solid border-amber-300 bg-amber-50 p-3 text-amber-900">
              <p className="font-medium">⚠️ Quedan sin responsable en Metas ({huerfanas.length}):</p>
              <ul className="mt-1 list-disc pl-5">
                {huerfanas.map((t) => (
                  <li key={`${t.cadena}-${t.num}`}>{nombreTienda(t)}</li>
                ))}
              </ul>
              <p className="mt-2">Asigne a quien lo sustituye en «Responsables por tienda».</p>
            </div>
          ) : (
            <p>Ninguna tienda quedó sin responsable.</p>
          )}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      header="Dar de baja por rotación"
      visible
      onHide={onHide}
      style={{ width: '34rem' }}
      breakpoints={{ '640px': '95vw' }}
      dismissableMask={!ocupado}
      footer={
        <div className="flex justify-end gap-2">
          <Button label="Cancelar" className="p-button-text" onClick={onHide} disabled={ocupado} />
          <Button
            label="Dar de baja"
            icon="pi pi-user-minus"
            className="p-button-danger"
            onClick={() => void enviar()}
            loading={ocupado}
            disabled={ocupado || !!bloqueo}
          />
        </div>
      }
    >
      <div className="space-y-4 text-sm text-gray-700">
        <p>
          Para <b>{quien}</b>, en un solo paso:
        </p>
        <ol className="list-decimal space-y-1 pl-5">
          <li>cierra todas sus sesiones y conexiones en vivo;</li>
          <li>desactiva la cuenta;</li>
          <li>
            retira sus alcances ({alcances}) y su perfil
            {usuario.perfil ? ` (${usuario.perfil.nombre})` : ''};
          </li>
          <li>deja registro de quién, cuándo y por qué.</li>
        </ol>
        {(usuario.alcancesNegocio ?? []).length > 0 && (
          <p className="text-xs text-gray-500">
            Consulta CM: {(usuario.alcancesNegocio ?? []).map(etiquetaAlcanceNegocio).join(', ')}.
          </p>
        )}
        <p className="text-xs text-gray-500">
          Si se restaura la cuenta, sus sesiones viejas no reviven y sus alcances no vuelven solos.
        </p>
        {bloqueo && <Message severity="warn" text={bloqueo} className="w-full justify-start" />}
        <MotivoCampos motivo={motivo} nota={nota} onMotivo={setMotivo} onNota={setNota} disabled={ocupado || !!bloqueo} />
        {error && <Message severity="error" text={error} className="w-full justify-start" />}
      </div>
    </Dialog>
  );
};
